import { useCallback, useEffect, useState } from "react";
import {
  Card,
  Col,
  Row,
  Statistic,
  message,
} from "antd";
import { api, type Health } from "../api";
import { useSSE } from "../hooks/useSSE";
import LiveChart from "../components/LiveChart";
import TopTalkersTable from "../components/TopTalkersTable";
import DashboardGrid, { type WidgetDef } from "../components/DashboardGrid";

// ── widget definitions ────────────────────────────────────────────────────────

const WIDGETS: WidgetDef[] = [
  {
    id: "chart-global",
    defaultLayout: { x: 0, y: 0, w: 12, h: 10 },
    minW: 4,
    minH: 5,
    title: "Bits/s :: Global",
    color: "#c0392b",
    icon: "≈",
    children: <LiveChart seconds={600} refreshMs={5000} height={200} />,
  },
  {
    id: "top-bps-in",
    defaultLayout: { x: 0, y: 10, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top Inbound Talkers :: Bits/s :: Live",
    color: "#4472C4",
    icon: "↓",
    children: <TopTalkersTable metric="bps" dir="in" color="#4472C4" />,
  },
  {
    id: "top-bps-out",
    defaultLayout: { x: 6, y: 10, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top Outbound Talkers :: Bits/s :: Live",
    color: "#ED7D31",
    icon: "↑",
    children: <TopTalkersTable metric="bps" dir="out" color="#ED7D31" />,
  },
  {
    id: "top-pps-in",
    defaultLayout: { x: 0, y: 21, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top Packets In :: Pkts/s :: Live",
    color: "#70AD47",
    icon: "↓",
    children: <TopTalkersTable metric="pps" dir="in" color="#70AD47" />,
  },
  {
    id: "top-pps-out",
    defaultLayout: { x: 6, y: 21, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top Packets Out :: Pkts/s :: Live",
    color: "#FF4D4F",
    icon: "↑",
    children: <TopTalkersTable metric="pps" dir="out" color="#FF4D4F" />,
  },
  {
    id: "top-ixp-bps-in",
    defaultLayout: { x: 0, y: 32, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top IXP Inbound :: Bits/s :: Live",
    color: "#2e86de",
    icon: "↓",
    children: <TopTalkersTable metric="bps" dir="in" color="#2e86de" geo="ixp" />,
  },
  {
    id: "top-ixp-bps-out",
    defaultLayout: { x: 6, y: 32, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top IXP Outbound :: Bits/s :: Live",
    color: "#e67e22",
    icon: "↑",
    children: <TopTalkersTable metric="bps" dir="out" color="#e67e22" geo="ixp" />,
  },
];

// ── main page ─────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const [health, setHealth] = useState<Health | null>(null);
  const [loading, setLoading] = useState(true);
  const { tick, connected } = useSSE("/api/v1/events/stream");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const h = await api.health();
      setHealth(h);
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const lastTickAgo = tick
    ? `${Math.max(0, Math.floor(Date.now() / 1000 - tick.ts))}s ago`
    : "—";

  void loading; // suppress unused warning

  return (
    <>
      {/* Status row */}
      <Row gutter={8} style={{ marginBottom: 8 }}>
        {[
          { title: "API", value: health?.status ?? "…", ok: health?.status === "ok" },
          { title: "Uptime", value: health?.uptime ?? "…", ok: true },
          { title: "Database", value: health?.db ? "up" : "down", ok: health?.db },
          { title: "Live stream", value: connected ? lastTickAgo : "disconnected", ok: connected },
        ].map(({ title, value, ok }) => (
          <Col span={6} key={title}>
            <Card size="small">
              <Statistic
                title={title}
                value={value}
                valueStyle={{ fontSize: 18, color: ok ? "#3f8600" : "#cf1322" }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Draggable / resizable widget grid */}
      <DashboardGrid widgets={WIDGETS} />
    </>
  );
}
